/**
 * Named movement configurations ("presets") saved per browser. A preset holds
 * movement overrides relative to DEFAULT_CONFIG plus the experiment rule
 * state. Imported data is untrusted: unknown keys are rejected and every
 * value must be finite and inside the Tunable range, so a loaded preset is
 * always a valid configuration.
 */
import { DEFAULT_CONFIG, type MovementConfig } from '../core/Config';
import type { MovementRuleSet } from '../movement/MovementRules';
import { TUNABLES } from './Tunables';

export interface TuningPreset {
  version: 1;
  name: string;
  movement: Partial<Record<keyof MovementConfig, number>>;
  rules: { enabled: string[]; params: Record<string, Record<string, number>> };
}

export class PresetError extends Error {}

const STORAGE_KEY = 'xernous.presets.v1';

/** Read-only presets shipped with the game (Phase 3 movement models; see docs/research/MOMENTUM_MODEL.md). */
export const BUILTIN_PRESETS: readonly TuningPreset[] = Object.freeze([
  { version: 1, name: '[built-in] Lead steering (C3)', movement: { airLeadGain: 1.6, airLeadSideGain: 0.8, airLeadAngleDeg: 25 }, rules: { enabled: [], params: {} } },
  { version: 1, name: '[built-in] Phase 2 baseline', movement: { contactContinuityDeg: 0, airSteerKeepsSpeed: 0 }, rules: { enabled: [], params: {} } },
] as const);
const MAX_NAME = 48;

const movementRanges = new Map(TUNABLES.flatMap((t) => (t.target.kind === 'movement' ? [[t.target.field, t] as const] : [])));

export function capturePreset(name: string, movement: Readonly<MovementConfig>, rules: MovementRuleSet): TuningPreset {
  const overrides: TuningPreset['movement'] = {};
  for (const [field] of movementRanges) {
    if (movement[field] !== DEFAULT_CONFIG.movement[field]) overrides[field] = movement[field];
  }
  const params: TuningPreset['rules']['params'] = {};
  for (const rule of rules.list()) params[rule.id] = { ...rule.params };
  return { version: 1, name: name.trim().slice(0, MAX_NAME) || 'untitled', movement: overrides, rules: { enabled: rules.enabledIds(), params } };
}

/** Validates untrusted data and returns a normalised preset, or throws PresetError. */
export function parsePreset(raw: unknown, rules: MovementRuleSet): TuningPreset {
  const fail = (msg: string): never => {
    throw new PresetError(msg);
  };
  if (typeof raw !== 'object' || raw === null) fail('Preset must be an object');
  const o = raw as Record<string, unknown>;
  if (o['version'] !== 1) fail('Unsupported preset version');
  const name = typeof o['name'] === 'string' ? o['name'].trim().slice(0, MAX_NAME) : fail('Preset name missing');
  const movement: TuningPreset['movement'] = {};
  const mv = (o['movement'] ?? {}) as Record<string, unknown>;
  if (typeof mv !== 'object') fail('movement must be an object');
  for (const [k, v] of Object.entries(mv)) {
    const t = movementRanges.get(k as keyof MovementConfig);
    if (!t) fail(`Unknown or non-tunable movement field "${k}"`);
    if (typeof v !== 'number' || !Number.isFinite(v) || v < t!.min || v > t!.max) fail(`movement.${k} must be within [${t!.min}, ${t!.max}]`);
    movement[k as keyof MovementConfig] = v as number;
  }
  const rr = (o['rules'] ?? { enabled: [], params: {} }) as Record<string, unknown>;
  const enabled = Array.isArray(rr['enabled']) ? rr['enabled'] : fail('rules.enabled must be an array');
  const known = new Set(rules.list().map((r) => r.id));
  for (const id of enabled as unknown[]) if (typeof id !== 'string' || !known.has(id)) fail(`Unknown rule "${String(id)}"`);
  const params: TuningPreset['rules']['params'] = {};
  for (const [id, p] of Object.entries((rr['params'] ?? {}) as Record<string, unknown>)) {
    if (!known.has(id)) fail(`Unknown rule "${id}"`);
    const specs = rules.get(id).paramSpecs as Record<string, { min: number; max: number }>;
    params[id] = {};
    for (const [k, v] of Object.entries((p ?? {}) as Record<string, unknown>)) {
      const spec = Object.hasOwn(specs, k) ? specs[k] : undefined;
      if (!spec) fail(`Unknown parameter ${id}.${k}`);
      if (typeof v !== 'number' || !Number.isFinite(v) || v < spec!.min || v > spec!.max) fail(`${id}.${k} must be within [${spec!.min}, ${spec!.max}]`);
      params[id]![k] = v as number;
    }
  }
  const enabledSet = new Set(enabled as string[]);
  for (const id of enabledSet) {
    for (const req of rules.get(id).requires ?? []) if (!enabledSet.has(req)) fail(`Rule "${id}" requires "${req}" to be enabled too`);
  }
  return { version: 1, name: name || 'untitled', movement, rules: { enabled: [...enabledSet], params } };
}

/**
 * Applies rule state from a preset: parameters first, then enablement in
 * dependency order. All-or-nothing: on any failure the previous rule state is
 * restored before the error propagates.
 */
export function applyPresetRules(preset: TuningPreset, rules: MovementRuleSet): void {
  const before: TuningPreset['rules'] = { enabled: rules.enabledIds(), params: Object.fromEntries(rules.list().map((r) => [r.id, { ...r.params }])) };
  try {
    setRuleState(preset.rules, rules);
  } catch (err: unknown) {
    setRuleState(before, rules);
    throw err instanceof PresetError ? err : new PresetError(err instanceof Error ? err.message : String(err));
  }
}

function setRuleState(state: TuningPreset['rules'], rules: MovementRuleSet): void {
  disableAll(rules);
  for (const [id, params] of Object.entries(state.params)) {
    for (const [k, v] of Object.entries(params)) rules.setParam(id, k, v);
  }
  const pending = [...state.enabled];
  // Enable in an order that satisfies `requires` (at most n passes).
  for (let pass = 0; pass < pending.length + 1 && pending.length > 0; pass++) {
    for (let i = pending.length - 1; i >= 0; i--) {
      const id = pending[i]!;
      const reqs = rules.get(id).requires ?? [];
      if (reqs.every((r) => rules.isEnabled(r))) {
        rules.enable(id);
        pending.splice(i, 1);
      }
    }
  }
  if (pending.length > 0) throw new PresetError(`Could not enable rules: ${pending.join(', ')}`);
}

function disableAll(rules: MovementRuleSet): void {
  // Disable dependents before their requirements.
  let progress = true;
  while (progress && rules.enabledIds().length > 0) {
    progress = false;
    for (const id of rules.enabledIds()) {
      const dependent = rules.enabledIds().some((other) => (rules.get(other).requires ?? []).includes(id));
      if (!dependent) {
        rules.disable(id);
        progress = true;
      }
    }
  }
}

export interface PresetStore {
  list(): TuningPreset[];
  save(p: TuningPreset): void;
  remove(name: string): void;
}

/** localStorage-backed store; corrupt entries are skipped, never thrown. */
export class LocalPresetStore implements PresetStore {
  constructor(private readonly rules: MovementRuleSet) {}

  list(): TuningPreset[] {
    try {
      const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]') as unknown;
      if (!Array.isArray(raw)) return [];
      return raw.flatMap((p) => {
        try {
          return [parsePreset(p, this.rules)];
        } catch {
          return [];
        }
      });
    } catch {
      return [];
    }
  }

  save(p: TuningPreset): void {
    const all = this.list().filter((x) => x.name !== p.name);
    all.push(p);
    this.write(all);
  }

  remove(name: string): void {
    this.write(this.list().filter((x) => x.name !== name));
  }

  private write(all: TuningPreset[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
    } catch (err: unknown) {
      console.warn('Presets could not be saved to this browser.', err);
    }
  }
}
